'use client';

import { useMemo, useCallback, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp,
  faChevronDown,
  faLocationDot,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  TreatmentToggle,
  PulseIconButton,
  ProviderSitesToggle,
} from 'src/components';
import { useAuth } from 'src/context/auth';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDiscoveryMapCurrentTerritory,
  selectAdvancedSearchRadius,
  clearAdvancedSearchRadius,
} from 'src/redux/slices/discoveryMapSlice';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { USER_SETTINGS } from 'src/hooks/users/constants';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useIsAdvancedSearchActive from 'src/hooks/discovery/useIsAdvancedSearchActive';
import { createUserActivity } from 'src/api';
import { TOGGLE_VIEW } from 'src/api/userActivities/types';
import TerritoryPanel from '../TerritoryPanel/TerritoryPanel';
import { SavedVolumesDropdown } from '../ResultsPanel/components/SavedVolumeDropdown/components';
import { CodePreview } from '../CodePreview';
import DateDropdown from './DateDropdown/DateDropdown';

const DiscoveryPanel = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const { user } = useAuth();
  const { settings, setSetting } = useUserSettings();
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const { openDrawer } = useDrawer();
  const dispatch = useDispatch();
  const isAdvancedSearchActive = useIsAdvancedSearchActive();
  const advancedSearchRadius = useSelector(selectAdvancedSearchRadius);
  const isMedScoutUser =
    user?.is_superuser || user?.permissions?.is_support_user;

  const handleIsDisabled = useCallback(
    (value: string) => {
      // Disable 'sending' mode if prospect type is 'C'
      return value === 'sending' && settings?.prospectType === 'C';
    },
    [settings?.prospectType]
  );

  const handleTreatmentToggleChange = useCallback(
    async (value) => {
      setSetting(USER_SETTINGS.PROSPECT_MODE, value);
      try {
        await createUserActivity({
          id: user?.id,
          data: { type: TOGGLE_VIEW.DISCOVERY_REFERRAL_TOGGLE },
        });
      } catch (error) {
        console.error('Failed to log user activity:', error);
      }
    },
    [setSetting, user?.id]
  );

  const handleOpenEncountersDrawer = useCallback(() => {
    openDrawer('encountersDrawer');
  }, [openDrawer]);

  const treatingValue = useMemo(() => {
    return settings?.prospectMode || 'treating';
  }, [settings?.prospectMode]);

  const entityValue = useMemo(() => {
    return settings?.prospectType || 'HCP';
  }, [settings?.prospectType]);

  const handleEntityChange = useCallback(
    (value: 'HCP' | 'C') => {
      setSetting(USER_SETTINGS.PROSPECT_TYPE, value);
    },
    [setSetting]
  );

  const handleClearRadius = useCallback(() => {
    dispatch(clearAdvancedSearchRadius());
  }, [dispatch]);

  // Clear ephemeral radius when the user leaves advanced search — but NOT on
  // initial mount, because the saved volume may not have propagated yet when
  // navigating from Scout AI ("View on Map" sets the radius before navigation).
  const [wasAdvancedSearchActive, setWasAdvancedSearchActive] = useState(isAdvancedSearchActive);
  useEffect(() => {
    if (wasAdvancedSearchActive && !isAdvancedSearchActive && advancedSearchRadius) {
      dispatch(clearAdvancedSearchRadius());
    }
    setWasAdvancedSearchActive(isAdvancedSearchActive);
  }, [isAdvancedSearchActive, advancedSearchRadius, dispatch, wasAdvancedSearchActive]);

  // Clear radius on unmount (navigating away from Discovery)
  useEffect(() => {
    return () => {
      dispatch(clearAdvancedSearchRadius());
    };
  }, [dispatch]);

  return (
    <section
      aria-label="Discovery filters"
      data-testid="discovery-panel"
      className={`absolute top-2 left-3 z-[100] rounded-lg overflow-hidden transition-all duration-300 ease-in-out ${
        isMinimized
          ? 'w-8 h-8 flex items-center justify-center shadow-xl bg-white hover:bg-neutral-200 cursor-pointer'
          : 'w-[calc(100%-24px)] px-4 pb-4 pt-2 bg-gradient-to-r from-sky-50 to-white'
      }`}
      onClick={isMinimized ? () => setIsMinimized(false) : undefined}
      role={isMinimized ? 'button' : undefined}
      tabIndex={isMinimized ? 0 : undefined}
      onKeyDown={
        isMinimized
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsMinimized(false);
              }
            }
          : undefined
      }
    >
      <PulseIconButton
        variant="plain"
        color="neutral"
        size="sm"
        onClick={() => setIsMinimized(!isMinimized)}
        icon={isMinimized ? faChevronDown : faChevronUp}
        aria-label={
          isMinimized ? 'Expand discovery panel' : 'Minimize discovery panel'
        }
        aria-expanded={!isMinimized}
        aria-controls="discovery-panel-content"
        tooltip={isMinimized ? 'Expand panel' : 'Minimize panel'}
        tooltipPosition="bottom"
        data-testid="discovery-panel-toggle"
        className={`flex-shrink-0 rounded-lg focus:ring-0 focus:ring-offset-0 ${
          isMinimized
            ? 'w-full h-full bg-transparent hover:bg-transparent pointer-events-none border-0'
            : 'border border-neutral-300'
        }`}
      />

      {/* Content wrapper with height animation */}
      <div
        id="discovery-panel-content"
        data-testid="discovery-panel-content"
        className={`transition-all duration-300 ease-in-out ${
          isMinimized
            ? 'h-0 opacity-0 overflow-hidden absolute'
            : 'h-auto opacity-100 mt-2'
        }`}
      >
        <div
          className="relative grid grid-cols-2 gap-x-4 gap-y-3 @[600px]:grid-cols-3 @[1150px]:grid-cols-[2fr_2fr_1fr_1fr_1fr_auto] items-end"
          role="group"
          aria-label="Discovery filter controls"
          data-testid="discovery-filter-controls"
        >
          <div className="flex flex-col min-w-0" data-testid="filter-territory">
            <div className="flex items-center">
              <label className="text-sm font-medium mb-1">Territory</label>
              {currentTerritory && isMedScoutUser && (
                <span className="ml-2 text-xs text-gray-500">
                  ({currentTerritory.id})
                </span>
              )}
            </div>
            <TerritoryPanel />
          </div>

          <div className="flex flex-col min-w-0" data-testid="filter-code-set">
            <label className="text-sm font-medium mb-1">Code Set</label>
            <SavedVolumesDropdown
              location={LOCATION.DISCOVERY}
              onOpenEncounters={handleOpenEncountersDrawer}
              showActions
            />
          </div>

          {isAdvancedSearchActive && advancedSearchRadius && (
            <div className="flex flex-col min-w-0" data-testid="filter-radius">
              <label className="text-sm font-medium mb-1">Radius</label>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 text-sky-700 px-3 py-1.5 text-sm font-medium">
                  <FontAwesomeIcon icon={faLocationDot} className="text-xs" />
                  {advancedSearchRadius.radius_miles} mi
                  <button
                    onClick={handleClearRadius}
                    className="hover:text-sky-900 transition-colors cursor-pointer"
                    aria-label="Clear radius filter"
                    data-testid="clear-radius-btn"
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-xs" />
                  </button>
                </span>
              </div>
            </div>
          )}

          {!isAdvancedSearchActive && (
            <div className="flex flex-col min-w-0" data-testid="filter-type">
              <label className="text-sm font-medium mb-1">Type</label>
              <ProviderSitesToggle
                value={entityValue}
                onChange={handleEntityChange}
              />
            </div>
          )}

          {!isAdvancedSearchActive && (
            <div className="flex flex-col min-w-0" data-testid="filter-source">
              <label className="text-sm font-medium mb-1">Source</label>
              <TreatmentToggle
                value={treatingValue}
                onChange={handleTreatmentToggleChange}
                isDisabled={handleIsDisabled}
              />
            </div>
          )}

          {!isAdvancedSearchActive && (
            <div
              className="flex flex-col min-w-0"
              data-testid="filter-year-range"
            >
              <label className="text-sm font-medium mb-1">Year Range</label>
              <DateDropdown />
            </div>
          )}

          <div className="flex items-end" data-testid="filter-code-preview">
            <CodePreview />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiscoveryPanel;
